import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown, MapPin, X } from "lucide-react";
import { groupLocationsByTown, useActiveLocations } from "./locationGroups";

const LocationDropdown = ({ buttonText = "S'inscrire aux hebdos" }) => {
  const [open, setOpen] = useState(false);
  const locations = useActiveLocations();
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const panelId = useId();
  const { pathname } = useLocation();

  const currentId = pathname.match(/^\/locations\/([^/]+)/)?.[1];
  const groups = useMemo(() => groupLocationsByTown(locations || []), [locations]);

  const close = (restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus) buttonRef.current?.focus();
  };

  // Close on outside click and on navigation
  useEffect(() => {
    if (!open) return;
    const onPointer = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
    };
  }, [open]);
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Escape closes; arrows move through the list. Listened on the document because
  // clicking the button doesn't focus it in Safari.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        buttonRef.current?.focus();
        return;
      }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      const list = [...(listRef.current?.querySelectorAll("[data-location-item]") || [])];
      if (list.length === 0) return;
      e.preventDefault();
      const index = list.indexOf(document.activeElement);
      if (e.key === "ArrowDown") list[index < 0 ? 0 : Math.min(index + 1, list.length - 1)].focus();
      else list[Math.max(index - 1, 0)].focus();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  return (
    <div ref={rootRef} className="relative lg:ml-8">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={(e) => {
          if (!open && e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
          }
        }}
        aria-expanded={open}
        aria-controls={panelId}
        aria-haspopup="true"
        className={`flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 md:px-4 py-2 text-sm font-bold text-white shadow-sm transition
          focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900
          ${open ? "bg-amber-600" : "bg-amber-500 hover:bg-amber-600"}`}
      >
        {buttonText}
        <ChevronDown
          size={16}
          strokeWidth={2.5}
          className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <>
            {/* Dim the page behind the sheet on phones */}
            <motion.div
              key="backdrop"
              className="fixed inset-0 z-40 bg-black/50 sm:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => close()}
              aria-hidden="true"
            />
            <motion.div
              key="panel"
              id={panelId}
              initial={{ opacity: 0, y: -8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.16, ease: "easeOut" }}
              style={{ transformOrigin: "top right" }}
              className="fixed inset-x-3 top-14 z-50 flex max-h-[calc(100dvh-5rem)] flex-col overflow-hidden rounded-xl
                border border-slate-700/80 bg-slate-900 text-left text-slate-100 shadow-2xl shadow-black/50
                sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-72 sm:max-h-[min(75vh,36rem)]"
            >
              {/* Phones only: title and close button */}
              <div className="flex items-center justify-between gap-3 border-b border-slate-800 py-2 pl-4 pr-2 sm:hidden">
                <p className="text-sm font-semibold text-white">Tournois hebdomadaires</p>
                <button
                  type="button"
                  onClick={() => close(true)}
                  className="rounded-full p-1.5 text-slate-400 transition hover:bg-slate-800 hover:text-white"
                  aria-label="Fermer"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Grouped list */}
              <div ref={listRef} className="flex-1 overflow-y-auto overscroll-contain pb-2 sm:pt-1">
                {!locations ? (
                  <div className="space-y-2 p-4" aria-label="Chargement">
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} className="h-9 animate-pulse rounded-lg bg-slate-800" />
                    ))}
                  </div>
                ) : groups.length === 0 ? (
                  <p className="px-4 py-8 text-center text-sm text-slate-400">
                    Aucun tournoi hebdomadaire pour le moment.
                  </p>
                ) : (
                  groups.map((group) => (
                    <section key={group.town} aria-label={group.town}>
                      <h3 className="sticky top-0 z-10 flex items-center gap-1.5 bg-slate-900/95 px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wider text-amber-400 backdrop-blur">
                        <MapPin size={12} strokeWidth={2.5} />
                        {group.town}
                      </h3>
                      <div className="px-2">
                        {group.locations.map((loc) => {
                          const current = loc.id === currentId;
                          return (
                            <Link
                              key={loc.id}
                              to={`/locations/${loc.id}`}
                              data-location-item
                              aria-current={current ? "page" : undefined}
                              onClick={() => close()}
                              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition focus:outline-none
                                ${current
                                  ? "bg-amber-500/10 text-amber-200"
                                  : "text-slate-100 hover:bg-slate-800 focus:bg-slate-800"}`}
                            >
                              <span className="min-w-0 flex-1 truncate font-medium">
                                {loc.name || loc.id}
                                {loc.sharedName && loc.table && (
                                  <span className="font-normal text-slate-400"> · {loc.table}</span>
                                )}
                              </span>
                              {current && <Check size={16} className="shrink-0 text-amber-400" aria-label="Lieu actuel" />}
                            </Link>
                          );
                        })}
                      </div>
                    </section>
                  ))
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LocationDropdown;
